import { type CaraWorkspaceFieldsFragment } from '@oysterjs/ais/api/graphql';

type StageEvent = CaraWorkspaceFieldsFragment['planStages'][0]['events'][0];

/** What the DRAFT_EMAIL card shows. */
export type DraftEmailSummary = {
  subject: string;
  /** The To line as one display string; empty until recipients arrive. */
  recipients: string;
  /** Flattened HTML for counts, or the original Markdown from pre-HTML tool calls. */
  bodyText: string;
  /** Source HTML for readable previews and handoffs; `bodyText` still measures the body. */
  bodyHtml?: string;
  /** The EMAIL artifact the call wrote, once it has one. */
  artifactId?: string;
};

const recipientLine = (recipients: { email: string }[] | null | undefined): string =>
  (recipients ?? [])
    .map((recipient) => recipient.email)
    .filter(Boolean)
    .join(', ');

/**
 * Bounded because a body streaming in presents a different, longer string on
 * every frame, and an unbounded cache would hold every intermediate draft for
 * the life of the tab. Sized for the handful of email cards a conversation
 * shows at once.
 */
const PLAIN_TEXT_CACHE_LIMIT = 64;
const plainTextCache = new Map<string, string>();

/**
 * Strips markup for the card's preview. Parsing into a detached document runs
 * no script and loads no resource, which matters because the body is model
 * output.
 *
 * Results are cached: the card is rendered from a plain function, not a
 * component, so it reparses on every render of the conversation around it —
 * and the conversation re-renders on every frame of whatever else is streaming.
 */
export const htmlToPlainText = (html: string): string => {
  if (!html) return '';
  if (typeof DOMParser === 'undefined') return html;

  const cached = plainTextCache.get(html);
  if (cached !== undefined) return cached;

  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const text = (parsed.body.textContent ?? '').trim();

  if (plainTextCache.size >= PLAIN_TEXT_CACHE_LIMIT) {
    const oldest = plainTextCache.keys().next();
    if (!oldest.done) plainTextCache.delete(oldest.value);
  }
  plainTextCache.set(html, text);

  return text;
};

/**
 * Reads a DRAFT_EMAIL stage event, or answers undefined while the call is
 * still only a placeholder.
 *
 * Five field shapes are read. CREATE_DRAFT and UPDATE_DRAFT each carry the
 * email in their own sub-object (UPDATE_DRAFT as a patch, so some fields may be
 * absent); CREATE_FROM_TEMPLATE carries only the recipients, since the server
 * writes the subject and body, and reports that body in the output; a call
 * written before there were operations
 * carries it flat on the input; and `toEmail`/`body` are what the tool recorded before it composed
 * whole recipient lists and wrote HTML bodies. Drafts of every generation are
 * still in workspaces people scroll back through.
 */
export const draftEmailSummary = (event: StageEvent): DraftEmailSummary | undefined => {
  const input = event.toolCall?.draftEmailTool?.input;
  if (!input) return undefined;
  const output = event.toolCall?.draftEmailTool?.output;

  const email = input.createDraft ?? input.updateDraft ?? input;
  const subject = email.subject ?? '';
  const to = input.createFromTemplate?.to ?? email.to;
  const bodyHtml = email.bodyHtml ?? output?.bodyHtml;
  const summary = {
    subject,
    recipients: to?.length ? recipientLine(to) : (input.toEmail ?? ''),
    bodyText: bodyHtml != null ? htmlToPlainText(bodyHtml) : (input.body ?? '').trim(),
    bodyHtml: bodyHtml ?? undefined,
    artifactId: output?.artifactId || input.updateDraft?.artifactId || input.artifactId || undefined
  };

  // Arguments arrive a character at a time, and the first frames of a call
  // carry nothing to summarise. Answering undefined leaves the placeholder up
  // rather than flashing an empty card. An UPDATE_DRAFT that has named its
  // artifact but not yet streamed a field still counts: the artifact fills the
  // card once the version lands.
  if (!summary.subject && !summary.recipients && !summary.bodyText && !summary.artifactId) {
    return undefined;
  }

  return summary;
};

/**
 * What an email revision card shows for one DRAFT_EMAIL call.
 *
 * The tool call is the snapshot for this event. The artifact row fills only
 * what the call left out (UPDATE_DRAFT is a patch) and never overwrites words the
 * call already carried — otherwise every card would track the live draft and
 * look identical. A superseded version's emptied body likewise leaves the tool
 * call's words in place.
 */
export const versionDraftEmail = (
  summary: DraftEmailSummary,
  artifact: CaraWorkspaceFieldsFragment['artifacts'][0] | undefined
): DraftEmailSummary => {
  const email = artifact?.email;
  if (!email) return summary;

  // When the tool call already carried a body, keep it — including leaving
  // bodyHtml unset so a later artifact body cannot replace the plain-text
  // snapshot through the handoff path that prefers HTML.
  const hasToolBody = Boolean(summary.bodyText || summary.bodyHtml);

  return {
    ...summary,
    subject: summary.subject || email.subject,
    recipients: summary.recipients || recipientLine(email.to),
    bodyText: hasToolBody
      ? summary.bodyText || (summary.bodyHtml ? htmlToPlainText(summary.bodyHtml) : '')
      : email.bodyHtml
        ? htmlToPlainText(email.bodyHtml)
        : '',
    bodyHtml: hasToolBody ? summary.bodyHtml : email.bodyHtml || undefined
  };
};
